/**
 * Primitives — عناصر الواجهة الأساسية المشتقة من Component Inventory المعتمد.
 * لا تحتوي أي منطق مجال أو حساب.
 */

import type { CSSProperties, ReactNode } from 'react';
import { color } from '@/design/tokens';

// ------------------------------------------------------------------ Card

export function Card({
  children,
  variant = 'primary',
  style,
  as: Tag = 'div',
}: {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'hero';
  style?: CSSProperties;
  as?: 'div' | 'section' | 'article';
}): JSX.Element {
  const cls =
    variant === 'secondary'
      ? 'nq-card nq-card--secondary'
      : variant === 'hero'
        ? 'nq-card nq-card--hero'
        : 'nq-card';
  return (
    <Tag className={cls} style={style}>
      {children}
    </Tag>
  );
}

// ---------------------------------------------------------------- Button

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  /** ثلاث درجات لا أكثر: فعل الشاشة · ثانوي · هامشي. */
  variant?: 'primary' | 'ghost' | 'quiet';
  /** `sm` للأفعال داخل الصفوف والترويسات. */
  size?: 'md' | 'sm';
  block?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
}): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[
        'nq-btn',
        `nq-btn--${variant}`,
        size === 'sm' ? 'nq-btn--sm' : '',
        block ? 'nq-btn--block' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ Chip

export function Chip({
  label,
  selected = false,
  onClick,
}: {
  label: string;
  selected?: boolean;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button type="button" className="nq-chip" aria-pressed={selected} onClick={onClick}>
      {label}
    </button>
  );
}

// ----------------------------------------------------- Segmented Control

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <div className="nq-seg" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <Chip
          key={o.value}
          label={o.label}
          selected={o.value === value}
          onClick={() => onChange(o.value)}
        />
      ))}
    </div>
  );
}

// --------------------------------------------------------- Numeric Field

export function NumericField({
  id,
  label,
  unit,
  value,
  onChange,
  placeholder,
  error,
  hint,
}: {
  id: string;
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string | null;
  hint?: string | null;
}): JSX.Element {
  return (
    <div style={{ flex: '1 1 210px', minWidth: 0 }}>
      <label htmlFor={id} className="nq-label" style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </label>
      {/*
        الغلاف لاتيني الاتجاه عمداً — كالحقل نفسه.
        العلّة التي أوجبته: الحقل `dir="ltr"` فنصه يبدأ يساراً، بينما الغلاف كان
        يرث اتجاه الصفحة العربي فتهبط اللاحقة يساراً هي الأخرى — فوق النص —
        وتُحجز لها مساحة في الجهة الفارغة. توحيد الاتجاه يجعل الحشو واللاحقة
        في جهة واحدة: «750 م²» لا «م750».
      */}
      <div dir="ltr" style={{ position: 'relative' }}>
        <input
          id={id}
          className="nq-input nq-num"
          inputMode="numeric"
          dir="ltr"
          style={{ textAlign: 'start', paddingInlineEnd: 56 }}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            insetInlineEnd: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 12,
            color: color.label,
            pointerEvents: 'none',
          }}
        >
          {unit}
        </span>
      </div>
      {hint ? (
        <p style={{ marginTop: 7, fontSize: 12, color: color.textSubtle }}>{hint}</p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" style={{ marginTop: 7, fontSize: 12, color: color.red }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------- KPI Cell

export function KpiCell({
  label,
  value,
  unit,
  tone = 'default',
}: {
  label: string;
  value: string;
  unit?: string;
  tone?: 'default' | 'up' | 'down' | 'warn' | 'accent';
}): JSX.Element {
  const toneColor =
    tone === 'up'
      ? color.green
      : tone === 'down'
        ? color.red
        : tone === 'warn'
          ? color.amber
          : tone === 'accent'
            ? color.teal
            : color.text;
  return (
    <div>
      <div className="nq-label">{label}</div>
      <div
        className="nq-num"
        dir="ltr"
        style={{
          marginTop: 6,
          fontSize: 30,
          fontWeight: 600,
          letterSpacing: '-.03em',
          color: toneColor,
          textAlign: 'start',
        }}
      >
        {value}
      </div>
      {unit ? (
        <div style={{ fontSize: 11.5, color: color.micro, marginTop: 2 }}>{unit}</div>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------- Section Title

export function SectionTitle({
  title,
  action,
}: {
  title: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 14,
      }}
    >
      <h2 className="nq-section-title">{title}</h2>
      {action}
    </div>
  );
}

// ----------------------------------------------------------------- Toast

export function Toast({ message }: { message: string }): JSX.Element {
  return (
    <div className="nq-toast" role="status">
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: '50%', background: color.green }}
      />
      <span>{message}</span>
    </div>
  );
}
